import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

import { RelativeTime, SectionCard, TablePagination, formatNumber } from '../../../../components/design-system/admin';
import { VendorView } from './vendor-view.component';
import { VendorCreditsCell } from './vendor-credits-cell.component';
import {
    ProductsCount,
    ROWS_PER_PAGE_OPTIONS,
    UsageValue,
    VendorActionsMenu,
    VendorCollectionProps,
    VendorLogo,
    VendorStatus,
    VisualizationsWithChange,
    isVendorEnabled,
    usageOf,
} from './vendor-row-parts.component';

const SKELETON_ROWS = 5;

/**
 * The vendors table: who they are, their plan and status, the last 30 days of
 * usage (from the admin overview) and their Content Studio credits. The name
 * opens the vendor; the logo opens a quick view.
 */
export const VendorsAsList = (props: VendorCollectionProps) => {
    const { t } = useTranslation();
    const [selectedVendorIdForView, setSelectedVendorIDForView] = useState<string | null>(null);
    const { vendors, usage, pagination, loading } = props;
    const firstLoad = loading && vendors.length === 0;

    return (
        <SectionCard
            title={t('ADMIN_VENDORS.LIST.TITLE')}
            description={
                usage.state === 'failed' ? t('ADMIN_VENDORS.LIST.USAGE_FAILED') : t('ADMIN_VENDORS.LIST.DESCRIPTION')
            }
            flush
            testId="vendors-table"
        >
            <div className="overflow-x-auto border-t" aria-busy={loading}>
                <Table>
                    <TableHeader>
                        <TableRow className="hover:bg-transparent">
                            <TableHead className="ps-4">{t('ADMIN_VENDORS.LIST.COL_VENDOR')}</TableHead>
                            <TableHead>{t('ADMIN_VENDORS.LIST.COL_PLAN')}</TableHead>
                            <TableHead>{t('ADMIN_VENDORS.LIST.COL_STATUS')}</TableHead>
                            <TableHead className="text-end">{t('ADMIN_VENDORS.LIST.COL_PRODUCTS')}</TableHead>
                            <TableHead className="text-end">{t('ADMIN_VENDORS.LIST.COL_VISUALIZATIONS')}</TableHead>
                            <TableHead className="text-end">{t('ADMIN_VENDORS.LIST.COL_LEADS')}</TableHead>
                            <TableHead>{t('ADMIN_VENDORS.LIST.COL_LAST_ACTIVITY')}</TableHead>
                            <TableHead>{t('ADMIN_VENDORS.LIST.COL_CREDITS')}</TableHead>
                            <TableHead className="w-12 pe-4">
                                <span className="sr-only">{t('ADMIN_SHELL.COMMON.ACTIONS')}</span>
                            </TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody className={cn(loading && !firstLoad && 'opacity-60 transition-opacity')}>
                        {firstLoad
                            ? Array.from({ length: SKELETON_ROWS }).map((_, i) => (
                                  <TableRow key={i} className="hover:bg-transparent">
                                      <TableCell className="ps-4" colSpan={9}>
                                          <div className="flex items-center gap-3">
                                              <Skeleton className="size-9 rounded-lg" />
                                              <div className="flex flex-col gap-1.5">
                                                  <Skeleton className="h-3.5 w-40" />
                                                  <Skeleton className="h-3 w-28" />
                                              </div>
                                          </div>
                                      </TableCell>
                                  </TableRow>
                              ))
                            : vendors.map((vendor) => {
                                  const row = usageOf(usage, vendor);
                                  return (
                                      <TableRow
                                          key={vendor._id}
                                          className={cn(!isVendorEnabled(vendor) && 'text-muted-foreground')}
                                          data-testid="vendor-row"
                                      >
                                          <TableCell className="ps-4">
                                              <div className="flex min-w-56 items-center gap-3">
                                                  <button
                                                      type="button"
                                                      className="shrink-0 rounded-lg transition-opacity hover:opacity-80 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
                                                      onClick={() => setSelectedVendorIDForView(vendor._id)}
                                                      aria-label={t('ADMIN_VENDORS.LIST.VIEW_DETAILS', { name: vendor.name })}
                                                  >
                                                      <VendorLogo vendor={vendor} className="size-9" />
                                                  </button>
                                                  <div className="min-w-0">
                                                      <Link
                                                          to={`/vendors/${vendor._id}/edit`}
                                                          className="block truncate font-medium hover:underline"
                                                      >
                                                          {vendor.name}
                                                      </Link>
                                                      <p className="truncate text-xs text-muted-foreground">
                                                          @{vendor.alias}
                                                          {vendor.email && <span className="ms-1.5">· {vendor.email}</span>}
                                                      </p>
                                                  </div>
                                              </div>
                                          </TableCell>
                                          <TableCell className="text-sm">
                                              <UsageValue usage={usage}>{row?.planName || '—'}</UsageValue>
                                          </TableCell>
                                          <TableCell>
                                              <VendorStatus vendor={vendor} />
                                          </TableCell>
                                          <TableCell className="text-end">
                                              <UsageValue usage={usage} end>
                                                  <ProductsCount vendor={vendor} row={row} />
                                              </UsageValue>
                                          </TableCell>
                                          <TableCell className="text-end">
                                              <UsageValue usage={usage} end>
                                                  <VisualizationsWithChange row={row} />
                                              </UsageValue>
                                          </TableCell>
                                          <TableCell className="text-end tabular-nums">
                                              <UsageValue usage={usage} end>
                                                  {row ? formatNumber(row.leads) : '—'}
                                              </UsageValue>
                                          </TableCell>
                                          <TableCell className="text-sm text-muted-foreground">
                                              <UsageValue usage={usage}>
                                                  {row ? <RelativeTime value={row.lastActivityAt} /> : '—'}
                                              </UsageValue>
                                          </TableCell>
                                          <TableCell>
                                              <VendorCreditsCell vendorId={vendor._id} />
                                          </TableCell>
                                          <TableCell className="pe-4 text-end">
                                              <VendorActionsMenu
                                                  vendor={vendor}
                                                  onEdit={props.handleEditVendor}
                                                  onToggleStatus={props.onToggleStatus}
                                                  onDelete={props.onDelete}
                                                  onMoveToProspects={props.onMoveToProspects}
                                              />
                                          </TableCell>
                                      </TableRow>
                                  );
                              })}
                    </TableBody>
                </Table>
            </div>

            <TablePagination
                className="border-t"
                page={Math.max(0, (pagination?.page || 1) - 1)}
                pageSize={pagination?.limit || 10}
                total={pagination?.total || 0}
                onPageChange={props.onPageChange}
                onPageSizeChange={props.onRowsPerPageChange}
                pageSizes={ROWS_PER_PAGE_OPTIONS}
            />

            {selectedVendorIdForView && (
                <VendorView
                    selectedVendorForViewID={selectedVendorIdForView}
                    setSelectedVendorForView={setSelectedVendorIDForView}
                    usage={usage.byId.get(String(selectedVendorIdForView))}
                    onEdit={props.handleEditVendor}
                />
            )}
        </SectionCard>
    );
};
